import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Alert,
  Box,
  Typography,
  Dialog as MuiDialog,

  TextField,
  FormHelperText,
  Divider,
  AlertTitle,
  DialogContentText,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  addVehicle,
  getVehicleInfo,
  searchDestination,
  searchTransportCompany,
} from "../../../../actions/formAction";
import { Formik, Form } from "formik";
import { baseUrl } from "../../../../util/constant";
import FileUpload from "../../../../components/FileUpload";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import * as Yup from "yup";
import DynamicDropDownlist from "../../../AddNewVehicles/components/DynamicDropDownlist";
import { useTranslation } from "react-i18next";
import Progress from "../../../../components/Loader/Progress ";
import { getFetchData } from "../../../../actions/fetchDataAction";
import { useLocation } from "react-router-dom";
import { resetState as addNewResetState } from "../../../../slices/addNewVehiclesSlice";

/* ---------- Section Title ---------- */
function SectionHeader({ title, color = "#0B3C5D" }) {
  return (
    <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ backgroundColor: color, width: 10, height: 22, borderRadius: 1 }} />
      <Typography variant="subtitle1" fontWeight="bold">
        {title}
      </Typography>
    </Box>
  );
}

/* ---------- Missing Data Fields ---------- */
function MissingDataFields({ formik, t }) {
  const url = baseUrl || "";

  const tcDisabled = Boolean(formik?.values?.status?.id > 2);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        <DynamicDropDownlist
          label="destination"
          formik={formik}
          fieldName="destination"
          disabled={false}
          loadingKey="isLoadingDestinationData"
          dataKey="destination"
          fetchAction={searchDestination}
          errorKey="destination"
        />

        <DynamicDropDownlist
          label="Transport Company"
          formik={formik}
          fieldName="transportCompany"
          disabled={tcDisabled}
          loadingKey="isLoadingTransportCompanyData"
          dataKey="transportCompany"
          fetchAction={searchTransportCompany}
          errorKey="transportCompany"
        />

        <Box display="flex" flexDirection="column">
          <TextField
            name="price"
            type="number"
            label={t("price")}
            value={formik.values.price ?? ""}
            onChange={formik.handleChange}
            error={formik.touched.price && Boolean(formik.errors.price)}
            inputProps={{ min: "0", step: "0.01" }}
          />
          {formik.touched.price && formik.errors.price && (
            <FormHelperText error>{formik.errors.price}</FormHelperText>
          )}
        </Box>

        <Box display="flex" flexDirection="column">
          <TextField
            name="weight"
            type="number"
            label={t("Weight")}
            value={formik.values.weight ?? ""}
            onChange={formik.handleChange}
            error={formik.touched.weight && Boolean(formik.errors.weight)}
            inputProps={{ min: "0", step: "0.01" }}
          />
          {formik.touched.weight && formik.errors.weight && (
            <FormHelperText error>{formik.errors.weight}</FormHelperText>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      <SectionHeader title={t("purchasePricePDF")} color="#d32f2f" />
      <FileUpload
        disabled={false}
        label={t("purchasePricePDF")}
        formikName="purchasePriceFile"
        uploadName="Purchase"
        accept=".pdf"
      />

      <SectionHeader title={t("Warehouse Pictures")} color="#1e88e5" />
      <FileUpload
        disabled={false}
        label={t("Warehouse Pictures")}
        uploadName="WarehousePictures"
        formikName="warehousePictures"
      />

      {/* Thumbnails */}
      <Box sx={{ display: "flex", flexWrap: "wrap", pt: 1, gap: 1.5 }}>
        {formik.values?.warehousePictures?.map((item) => (
          <Box key={item.id}>
            <a
              href={`${url}${item.filePath}`}
              download={item.fileName}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <img
                src={`${url}${item.filePath}`}
                alt={item.fileName}
                width={90}
                height={90}
                style={{ borderRadius: 6, border: "1px solid #eee", objectFit: "cover" }}
              />
            </a>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/* ---------- Modal ---------- */
function MissingDataModal({ isOpen, onClose, vehicleId }) {
  const [initialValues, setInitialValues] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
      const location = useLocation();
  
    const searchParams = new URLSearchParams(location.search);

  const vehicleInfo = useSelector((state) => state.vehicleInfo);
  const add = useSelector((state) => state.add);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen) {
      dispatch(getVehicleInfo({ vehicleId }));
    }

    return () => {


      dispatch(addNewResetState())


    
    }
   
  }, [dispatch, isOpen, vehicleId]);

  useEffect(() => {
    if (vehicleInfo.loading === "succeeded") {
      const v = vehicleInfo.Object || {};
      setInitialValues({
        ...v,
      });
    }
  }, [vehicleInfo.loading, vehicleInfo.Object]);

  const validationSchema = null
  const handleSave = async (values) => {
    await dispatch(addVehicle({ data: values, id: vehicleId }));
  };

  useEffect(() => {
    if (add.vehicleAddedSuccessfully) {
      setShowConfirm(false);
      onClose();
    }
    if (add.hasError) {
      setShowConfirm(false);
    }
  }, [add.vehicleAddedSuccessfully, add.hasError, onClose]);

  const missingSummary = useMemo(() => {
    const v = initialValues;
    const isArrived = v?.status?.id === 2;
    return [
      v?.price === 0 ? t("Price is missing") : null,
      isArrived && (!v?.weight || v?.weight === 0) ? t("Weight is missing") : null,
      !v?.transportCompany ? t("Transport Company is missing") : null,
      !v?.destination ? t("Destination is missing") : null,
      isArrived && (!v?.warehousePictures || v?.warehousePictures?.length < 5)
        ? t("Warehouse pictures are missing")
        : null,
    ].filter(Boolean);
  }, [initialValues, t]);

  return (
    <Formik initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={handleSave}>
      {(formik) => (
        <Form>
          <Dialog
            open={isOpen}
            onClose={(e, reason) => {
              if (add.isLoading) return;
              if (reason === "backdropClick") return;
              onClose();
            }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: "bold", pr: 5, position: "relative" }}>
              {t("Fix Missing Data")}
              <Button
                aria-label="close"
                onClick={onClose}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                  minWidth: 0,
                  p: 0,
                }}
              >
                <CloseIcon />
              </Button>
            </DialogTitle>

            <DialogContent dividers>
              {vehicleInfo.loading === "pending" || vehicleInfo.loading === "idle" ? (
                <Progress Msg={t("Loading vehicle information...")} />
              ) : vehicleInfo.loading === "failed" ? (
                <Alert severity="error">{t("Error loading vehicle information.")}</Alert>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Box
                    sx={{
                      background: "#f7fafd",
                      borderRadius: 2,
                      p: 2,
                      boxShadow: "0 1px 4px #0001",
                    }}
                  >
                    <SectionHeader title={t("Vehicle Information")} />
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {t("Client")}: {formik.values?.client?.name || "-"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {t("Vehicle")}: {formik.values?.year} {formik.values?.make?.name} {formik.values?.model?.name}{" "}
                      {formik.values?.vehicleType?.name}
                    </Typography>
                    <Typography variant="body2">{t("VIN")}: {formik.values?.vin || "-"}</Typography>

                    {missingSummary.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 1.5 }}>
                        <AlertTitle>{t("Missing data")}</AlertTitle>
                        {missingSummary.map((m, i) => (
                          <div key={i}>{m}</div>
                        ))}
                      </Alert>
                    )}
                  </Box>

                  <Box
                    sx={{
                      background: "#fff",
                      borderRadius: 2,
                      p: 2,
                      boxShadow: "0 1px 4px #0001",
                    }}
                  >
                    <SectionHeader title={t("Checklist")} />
                    <MissingDataFields formik={formik} t={t} />
                  </Box>
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1, justifyContent: "flex-end" }}>
              {add.hasError && (
                <Box className="errorBox" sx={{ width: "100%", mb: 1 }}>
                  <Alert severity="error">
                    <AlertTitle>{t("Error")}</AlertTitle>
                    {add.errorMessage || t("Something went wrong")}
                  </Alert>
                </Box>
              )}

              <Button
                color="secondary"
                variant="outlined"
                onClick={onClose}
                disabled={add.isLoading || formik.isSubmitting}
              >
                {t("Cancel")}
              </Button>
              <Button
                color="primary"
                variant="b1"
                startIcon={<SaveIcon />}
                disabled={add.isLoading || formik.isSubmitting}
                onClick={async () => {
                  await formik.validateForm();
                  setShowConfirm(true);
                }}
              >
                {t("Save")}
              </Button>
            </DialogActions>
          </Dialog>

          <MuiDialog
            open={showConfirm}
            onClose={(e, reason) => {
              if (add.isLoading) return;
              if (reason === "backdropClick") return;
              setShowConfirm(false);
            }}
          >
            <DialogTitle>{t("Save Confirmation")}</DialogTitle>
            <DialogContent>
              {Object.keys(formik.errors).length > 0 ? (
                <Box className="errorBox">
                  <Alert severity="error">
                    <AlertTitle>{t("Error")}</AlertTitle>
                    {Object.keys(formik.errors).map((fieldName, index) => {
                      const err = formik.errors[fieldName];
                      if (Array.isArray(err)) return null;
                      return <div key={index}>{String(err)}</div>;
                    })}
                  </Alert>
                </Box>
              ) : (
                <DialogContentText>
                  {t("Are you sure you want to confirm ?")}
                  <br />
                  <strong>{t("Client")}:</strong> {formik.values?.client?.name || "-"}
                  <br />
                  <strong>{t("Vehicle")}:</strong> {formik.values?.year} {formik.values?.make?.name}{" "}
                  {formik.values?.model?.name} {formik.values?.vehicleType?.name}
                  <br />
                  <strong>{t("VIN")}:</strong> {formik.values?.vin || "-"}
                </DialogContentText>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowConfirm(false)} color="secondary">
                {t("Cancel")}
              </Button>
              {!add.isLoading ? (
                <Button
                  onClick={() =>
                    Object.keys(formik.errors).length > 0 ? setShowConfirm(false) : formik.submitForm()
                  }
                  color="primary"
                  variant={Object.keys(formik.errors).length > 0 ? "b2" : "b1"}
                  startIcon={Object.keys(formik.errors).length > 0 ? <CloseIcon /> : <SaveIcon />}
                  disabled={formik.isSubmitting}
                >
                  {Object.keys(formik.errors).length > 0 ? t("Close") : t("Confirm Arrival")}
                </Button>
              ) : (
                <Box sx={{ pl: 2, pr: 2 }}>
                  <Progress Msg={t("Saving...")} />
                </Box>
              )}
            </DialogActions>
          </MuiDialog>
        </Form>
      )}
    </Formik>
  );
}

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  color: PropTypes.string,
};

MissingDataFields.propTypes = {
  formik: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

MissingDataModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vehicleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default MissingDataModal;